/**
 * agent-dm-stream-client
 *
 * Opens an SSE connection to `/api/agent/chat` for a Tauri DM coworker
 * response and feeds events into `agentDmStreamStore`.
 *
 * This is the Tauri DM equivalent of iOS's `AgentStreamClient.swift`.
 * Same wire protocol (the AI SDK data-stream format the rest of the
 * agent stack already uses), same server endpoint, same end behavior:
 * after the stream completes, the server-side tee accumulator in
 * `agent-routes.ts` posts the final message via `postAgentMessage` —
 * exactly one DB INSERT, exactly one unread trigger fire.
 */

import { agentDmStreamStore } from "./agent-dm-stream-store";
import { API_URL } from "./config";

// Per-channel in-flight controllers. Lets a second send for the same DM
// abort the first stream's network read so we don't keep two open SSE
// connections per channel. The store's trigger gating already prevents
// state pollution from stragglers, but an explicit abort also frees the
// underlying socket immediately. Server-side posting is unaffected — the
// tee accumulator at /api/agent/chat continues independently.
const inFlightByChannel = new Map<string, AbortController>();

/** Abort any in-flight stream for the channel without changing store state.
 *  Used by callers (e.g. StreamingDmBubble unmount cleanup). */
export function abortAgentDmStream(channelId: string): void {
  const ctrl = inFlightByChannel.get(channelId);
  if (ctrl) {
    inFlightByChannel.delete(channelId);
    try {
      ctrl.abort();
    } catch {
      /* ignore */
    }
  }
}

interface StartParams {
  /** Channel id (DM channel containing the coworker, or a regular
   *  channel where the coworker was @-mentioned). */
  channelId: string;
  /** Workspace id of the channel. */
  workspaceId: string;
  /** Coworker user id that should respond. Used as the agent message
   *  author placeholder while streaming. */
  coworkerId: string;
  /** The user message id that triggered this turn. Server uses it for
   *  dedup so the LISTEN/NOTIFY scheduler skips. */
  triggerMessageId: string;
  /** The user message text content. Server uses it as the latest
   *  user message in the conversation. */
  userMessageContent: string;
  /** Function that returns a fresh WorkOS access token. */
  getAccessToken: () => Promise<string | null>;
  /** Which kind of agent session this is:
   *    • `"dm"` (default) — 1:1 coworker DM, server infers coworker
   *      from channel_members.
   *    • `"mention"` — channel @-mention, server parses coworker id
   *      from the session_id tail. Requires `coworkerId` to match a
   *      channel member flagged `is_coworker = true`.
   */
  sessionPrefix?: "dm" | "mention";
}

/**
 * Fire-and-forget SSE consumer. Resolves once the stream finishes (or
 * errors). Errors are swallowed and surfaced via the store, NOT thrown
 * — the caller is the message-input handler and shouldn't be made to
 * await network IO.
 *
 * Concurrency: every store mutation is gated on `triggerMessageId`. If
 * a second `streamAgentDmResponse` for the same channel is started
 * before the first finishes, the second supersedes the first in the
 * store, and any straggler events from the first stream are dropped.
 *
 * Cancellation: callers can pass an `AbortSignal` (e.g. from a
 * `useEffect` cleanup that fires on channel unmount) to cancel the
 * fetch + drain the reader. Otherwise the SSE keeps running until the
 * server closes its end.
 */
export async function streamAgentDmResponse(
  params: StartParams & { signal?: AbortSignal },
): Promise<void> {
  const {
    channelId,
    workspaceId,
    coworkerId,
    triggerMessageId,
    userMessageContent,
    getAccessToken,
    signal: callerSignal,
    sessionPrefix = "dm",
  } = params;
  // DMs let the server infer the coworker from channel membership;
  // mentions need the coworker id baked into the session id so the
  // server knows *which* coworker is supposed to respond.
  const sessionId =
    sessionPrefix === "mention"
      ? `mention:${channelId}:${coworkerId}`
      : `dm:${channelId}`;

  // Abort any prior in-flight stream for this channel before starting a
  // new one. The store-level supersede is automatic via `start()`, but
  // the underlying fetch needs an explicit abort to release the socket.
  const prior = inFlightByChannel.get(channelId);
  if (prior) {
    try {
      prior.abort();
    } catch {
      /* ignore */
    }
  }
  const controller = new AbortController();
  inFlightByChannel.set(channelId, controller);

  // Forward caller-provided cancellation (e.g. unmount cleanup) into our
  // controller so both signals share a single abort path.
  const onCallerAbort = () => controller.abort();
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort();
    else callerSignal.addEventListener("abort", onCallerAbort, { once: true });
  }
  const signal = controller.signal;

  agentDmStreamStore.start(channelId, { coworkerId, triggerMessageId });

  // Single cleanup path: detach the caller's abort listener and remove
  // ourselves from the in-flight map (but only if we're still the active
  // controller — a newer turn may have replaced us already).
  const cleanup = () => {
    if (callerSignal) callerSignal.removeEventListener("abort", onCallerAbort);
    if (inFlightByChannel.get(channelId) === controller) {
      inFlightByChannel.delete(channelId);
    }
  };

  try {
    await runStream();
  } finally {
    cleanup();
  }
  return;

  // Inner runner so we can use early-returns without skipping cleanup.
  async function runStream(): Promise<void> {
    let token: string | null = null;
    try {
      token = await getAccessToken();
    } catch {
      /* fall through to nil-token error below */
    }
    if (!token) {
      agentDmStreamStore.fail(channelId, triggerMessageId, "Not authenticated");
      return;
    }

    let response: Response;
    try {
      response = await fetch(`${API_URL}/api/agent/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          // `dm:` / `mention:` prefix tells the server this is an agent
          // streaming session → it tees the stream and posts the final
          // message via the tee accumulator. DMs infer the target
          // coworker from channel_members; mentions parse it from the
          // session_id tail.
          session_id: sessionId,
          workspace_id: workspaceId,
          channel_id: channelId,
          trigger_message_id: triggerMessageId,
          messages: [{ role: "user", content: userMessageContent }],
        }),
        signal,
      });
    } catch (err) {
      if (signal?.aborted) {
        // The caller cancelled — leave whatever the store currently has,
        // don't post a noisy error. A new `start()` will reset on the
        // next send.
        return;
      }
      agentDmStreamStore.fail(
        channelId,
        triggerMessageId,
        err instanceof Error ? err.message : "Network error",
      );
      return;
    }

    // 202 = external or managed agent coworker — server won't stream,
    // but the agent will post its response via scheduler/webhook. Keep
    // the thinking pill visible; the auto-clear in StreamingDmBubble
    // will remove it once the real message arrives from Zero replication.
    // Managed agents can take up to 90s (tool execution loops).
    if (response.status === 202) {
      const isManaged =
        response.headers.get("content-type")?.includes("json") &&
        ((await response.json().catch(() => ({}))) as Record<string, unknown>)
          ?.managed === true;
      const timeoutMs = isManaged ? 95_000 : 20_000;
      setTimeout(() => {
        const cur = agentDmStreamStore.get(channelId);
        if (cur?.triggerMessageId === triggerMessageId && cur?.isStreaming) {
          agentDmStreamStore.fail(
            channelId,
            triggerMessageId,
            "No response from agent",
          );
        }
      }, timeoutMs);
      return;
    }

    if (!response.ok) {
      agentDmStreamStore.fail(
        channelId,
        triggerMessageId,
        `Agent returned HTTP ${response.status}`,
      );
      return;
    }

    const body = response.body;
    if (!body) {
      agentDmStreamStore.fail(channelId, triggerMessageId, "No response body");
      return;
    }

    // Parse the AI SDK data-stream protocol line-by-line. Format matches
    // the iOS `AgentStreamClient.swift` parser and the desktop terminal
    // `AgentDataStreamTransport.processResponseStream` translator:
    //
    //   0:"text token"\n          → text delta
    //   2:[{type:"thinking_delta",delta:"..."}]\n  → typed event
    //   d:{"finishReason":"stop"}\n                 → finish signal
    //   3:"error message"\n                         → error
    const decoder = new TextDecoder();
    let buffer = "";
    // Use a manual reader loop instead of `for await (...of body)` because
    // the desktop TS lib types don't expose `Symbol.asyncIterator` on
    // `ReadableStream` even though the runtime supports it.
    const reader = body.getReader();

    // First-chunk timeout — if the server returned 200 OK but the stream
    // never produces any bytes (Anthropic API hung, server worker crashed
    // mid-stream, etc.), abort after a window so the bubble doesn't lie
    // about agent state forever (Bug #9). Cleared as soon as the first
    // chunk arrives. Window matches the 202-path managed-agent timeout
    // (95s) so we don't accidentally cut off a slow-but-working tool
    // execution.
    const FIRST_CHUNK_TIMEOUT_MS = 95_000;
    let firstChunkSeen = false;
    const firstChunkTimer = setTimeout(() => {
      if (firstChunkSeen) return;
      try {
        controller.abort();
      } catch {
        /* ignore */
      }
      const cur = agentDmStreamStore.get(channelId);
      if (cur?.triggerMessageId === triggerMessageId && cur?.isStreaming) {
        agentDmStreamStore.fail(
          channelId,
          triggerMessageId,
          "Couldn't reach the agent — try again",
        );
      }
    }, FIRST_CHUNK_TIMEOUT_MS);

    try {
      for (;;) {
        if (signal?.aborted) {
          try {
            await reader.cancel();
          } catch {
            /* ignore */
          }
          return;
        }
        const { value, done } = await reader.read();
        if (done) break;
        if (!firstChunkSeen) {
          firstChunkSeen = true;
          clearTimeout(firstChunkTimer);
        }
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";
        for (const line of lines) handleLine(channelId, triggerMessageId, line);
      }
      if (buffer.trim()) handleLine(channelId, triggerMessageId, buffer);
    } catch (err) {
      if (signal?.aborted) return;
      agentDmStreamStore.fail(
        channelId,
        triggerMessageId,
        err instanceof Error ? err.message : "Stream error",
      );
      return;
    } finally {
      clearTimeout(firstChunkTimer);
    }

    // Stream ended cleanly. The server tee accumulator has already
    // INSERTed the real message; clearing the synthetic bubble lets the
    // Zero-replicated row take over. Trigger-gated `finish()` is a no-op
    // if a newer stream has already taken over the channel.
    agentDmStreamStore.finish(channelId, triggerMessageId);
  } // end runStream
}

function handleLine(
  channelId: string,
  triggerMessageId: string,
  rawLine: string,
): void {
  if (!rawLine) return;

  if (rawLine.startsWith("0:")) {
    try {
      const text = JSON.parse(rawLine.slice(2));
      if (typeof text === "string" && text.length > 0) {
        agentDmStreamStore.appendContent(channelId, triggerMessageId, text);
      }
    } catch {
      /* malformed text delta — skip */
    }
    return;
  }

  if (rawLine.startsWith("2:")) {
    try {
      const events = JSON.parse(rawLine.slice(2)) as Array<{
        type?: string;
        delta?: string;
        text?: string;
      }>;
      if (!Array.isArray(events)) return;
      for (const ev of events) {
        if (!ev || typeof ev !== "object") continue;
        if (ev.type === "thinking_delta" && typeof ev.delta === "string") {
          agentDmStreamStore.appendThinking(
            channelId,
            triggerMessageId,
            ev.delta,
          );
        } else if (
          ev.type === "assistant_final" &&
          typeof ev.text === "string"
        ) {
          agentDmStreamStore.setContent(channelId, triggerMessageId, ev.text);
        }
      }
    } catch {
      /* malformed typed event — skip */
    }
    return;
  }

  if (rawLine.startsWith("3:")) {
    try {
      const msg = JSON.parse(rawLine.slice(2));
      if (typeof msg === "string") {
        agentDmStreamStore.fail(channelId, triggerMessageId, msg);
      }
    } catch {
      agentDmStreamStore.fail(channelId, triggerMessageId, "Stream error");
    }
    return;
  }

  // `d:` finish events are handled implicitly: the for-await loop ends
  // when the server closes the body, and `streamAgentDmResponse` calls
  // `finish()` after the loop. Treating `d:` lines as no-ops keeps the
  // parser tolerant of payload variations.
}
